import { startOfDay } from "date-fns";
import { timeOfDaySchema, type ITimeOfDay } from "@medoma/models/activities";
import type { IRoute } from "@medoma/models/routes";

// Approximation: night time filter = overlaps multiple dates
const isNightTimeFilter = (timeFilter: { startTime: Date; endTime: Date }) =>
  startOfDay(timeFilter.startTime).getTime() !==
  startOfDay(timeFilter.endTime).getTime();

/**
 * Whether an activity with timeOfDay "Any" should be visible under the given time filter.
 *
 * "Any" is used for activities without a specific time slot. In practice these are
 * daytime activities, so they are shown during day-time filters but hidden during
 * night-time filters to avoid cluttering the night planner with irrelevant tasks.
 */
export const isAnyTimeOfDayVisible = (timeFilter: {
  startTime: Date;
  endTime: Date;
}) => !isNightTimeFilter(timeFilter);

/**
 * Whether any of the given occurrences overlaps the time window. "Any"
 * timeOfDay occurrences are treated as daytime (hidden under night filters).
 */
export const occurrencesOverlapTimeWindow = (
  occurrences: { start: Date; end: Date; timeOfDay: ITimeOfDay }[],
  timeFilter: { startTime: Date; endTime: Date },
) =>
  occurrences.some((occurrence) => {
    if (occurrence.timeOfDay === timeOfDaySchema.enum.Any) {
      return isAnyTimeOfDayVisible(timeFilter);
    }
    return (
      occurrence.end >= timeFilter.startTime &&
      occurrence.start <= timeFilter.endTime
    );
  });

export const filterRoutesByTimeWindow = (
  routes: IRoute[],
  timeFilter: { startTime: Date; endTime: Date } | null,
) => {
  if (!timeFilter) {
    return routes;
  }
  return routes.filter((route) =>
    route.visits.some((visit) =>
      occurrencesOverlapTimeWindow(visit.occurrences, timeFilter),
    ),
  );
};
